import React, {useCallback, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';

import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {LanguageSelector} from '@/widgets/LanguageSelector';
import {ROUTES} from '@app/routing';
import {PasswordGate, passwordModel} from '@entities/password/model';
import {history} from '@shared/lib/history';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Input} from '@shared/ui/Input';

import {
    ButtonContainer,
    Field,
    Fields,
    Form,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    MessageWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
} from './styled';

export function RequestResetPasswordForm() {
    const {t, i18n} = useTranslation();
    const password = useUnit(passwordModel);
    useGate(PasswordGate);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) => t(`password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                email: yup
                    .string()
                    .email(translateValidates('email'))
                    .required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, errors} = useForm({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {email: ''},
    });

    const onLogin = () => history.push(ROUTES.LOGIN);

    const onSubmit = async (data: {email: string}) => {
        try {
            await password.resetRequest({
                email: data.email,
                userName: data.email,
                firstName: data.email,
            });
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <div>
                <ServiceTitle size={4}>traceCORE B2B E-Invoicing</ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    {!password.state.success && (
                        <>
                            <PasswordTitle size={3}>
                                {t('password.resetRequest.title')}
                            </PasswordTitle>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormDescription>
                                    {t('password.resetRequest.description')}
                                </FormDescription>
                                <Fields>
                                    <Field isError={!!errors.email}>
                                        <span>{t('password.resetRequest.fields.email.label')}</span>
                                        <Controller
                                            name="email"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    placeholder={t(
                                                        'password.resetRequest.fields.email.placeholder',
                                                    )}
                                                    disabled={password.state.pending}
                                                    hasError={!!errors.email}
                                                    isValid={
                                                        formState.dirtyFields.email && !errors.email
                                                    }
                                                />
                                            )}
                                        />
                                    </Field>
                                </Fields>

                                <ButtonContainer>
                                    <Button
                                        type="submit"
                                        isLoading={password.state.pending}
                                        disabled={!formState.isValid}
                                    >
                                        {t('password.resetRequest.submitButton')}
                                    </Button>
                                    <Button onClick={onLogin} variant={ButtonVariant.Text}>
                                        {t('password.resetRequest.loginButton')}
                                    </Button>
                                </ButtonContainer>
                            </Form>
                        </>
                    )}
                    {password.state.success && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('password.resetRequest.success.title')}
                            </PasswordTitle>
                            <FormDescription>
                                {t('password.resetRequest.success.description')}
                            </FormDescription>
                            <Button onClick={onLogin}>
                                {t('password.resetRequest.success.backButton')}
                            </Button>
                        </MessageWrapper>
                    )}
                </PasswordContent>
            </MainContainer>
        </>
    );
}
